import { jsx as _jsx } from "react/jsx-runtime";
/**
 * UnfurlBlockHost
 *
 * Thin draggable wrapper around `BlockViewContainer` for blocks that
 * are unfurled inside an open Stack. Mirrors `SortableBlockViewHost`'s
 * role (it IS the direct `.stream-group__cards` child the FLIP +
 * StackFrame contracts depend on), but uses dnd-kit's `useDraggable`
 * instead of `useSortable`:
 *
 *   - The host isn't part of any `SortableContext`. The parent's
 *     SortableContext only tracks regular (in-grid) blocks; unfurled
 *     items shouldn't reorder inside the open stack, and shouldn't
 *     show up as sortable peers of the parent's grid blocks.
 *   - The drag's `data` carries `{ type: "stack-member", blockId,
 *     stackId, parentCollectionId }`, which `<Stream>`'s collision
 *     strategy + `onDragEnd` use to route the drop:
 *       - released on another Stack tile      → into that Stack (via
 *                                                a two-hop move through
 *                                                the parent)
 *       - released anywhere else (no `over`)  → out of the source
 *                                                Stack into the parent
 *                                                Collection
 *
 * The drag is gated to the steady-state `open` phase only. During
 * `opening` / `closing` the unfurl FLIP owns `style.transform` on
 * this host element, so letting dnd-kit also write to it would clash
 * — the disabled state keeps `useDraggable`'s transform pinned to
 * `null` (empty string at render) and the FLIP's direct DOM mutations
 * paint cleanly.
 *
 * Drag visual is the same restrained halo + z-lift used on
 * `SortableBlockViewHost` so picking up an unfurled card reads
 * identically to picking up a regular grid block.
 */
import React, { memo, useCallback, useMemo } from "react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { BlockViewContainer } from "@/components/block/block-view-container";
import "./unfurl-block-host.scss";
export const UnfurlBlockHost = memo(({ blockId, stackId, parentCollectionId, dateChipMode, isDragEnabled, disableDnd = false, registerUnfurlItemRef, }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: blockId,
        data: {
            type: "stack-member",
            blockId,
            stackId,
            parentCollectionId,
        },
        disabled: disableDnd || isDragEnabled === false,
    });
    // The unfurled card is ALSO a droppable target. Combined with the
    // stack tile's droppable, this makes the entire occupied area
    // inside the stack frame accept "drop into this stack" — drag a
    // block from the parent grid (or from a different Stack's unfurl)
    // and release over any of the unfurled cards to add it. Same
    // `type: "stack"` data as the tile, so the Stream's `onDragEnd`
    // routes both through one branch.
    //
    // Same-stack releases (a stack-member of THIS stack dropped back
    // on its own unfurled siblings) are a no-op via the Stream's
    // `sourceStackId === targetStackId` guard — keeping the droppable
    // active for same-stack drags also means short drift releases
    // INSIDE the frame stay inside the stack instead of triggering the
    // "no over → move out" branch.
    const droppableData = useMemo(() => ({
        type: "stack",
        stackId,
        parentCollectionId,
    }), [stackId, parentCollectionId]);
    const { setNodeRef: setDroppableRef } = useDroppable({
        id: `unfurl-drop:${blockId}`,
        data: droppableData,
        disabled: disableDnd,
    });
    // Combined ref — dnd-kit's draggable + droppable both need the DOM
    // node, AND `useStackUnfurl.registerItemRef` needs the same node
    // (the direct grid child) so its FLIP math sees the right element.
    // All three fire on every mount/unmount cycle.
    const mergedRef = useCallback((el) => {
        setNodeRef(el);
        setDroppableRef(el);
        registerUnfurlItemRef?.(blockId, el);
    }, [setNodeRef, setDroppableRef, registerUnfurlItemRef, blockId]);
    // `CSS.Translate.toString(null) === ""`. Letting React write the
    // empty string when there's no drag in flight is intentional: it
    // matches what the FLIP cleanup leaves on the DOM, so React's style
    // diff sees no change and never clobbers the FLIP's direct
    // mutations during `opening` / `closing`.
    //
    // While actively dragging, also publish the live drag delta as CSS
    // custom properties so the `.unfurl-block--dragging` pseudo-element
    // placeholder can counter-translate to stay glued to the source
    // slot — see the matching rule in `unfurl-block-host.scss`. Mirrors
    // the `--drag-offset-x` / `--drag-offset-y` contract on
    // `SortableBlockViewHost` so the dotted-outline UX reads identically
    // whether the picked-up card came from the grid or from an open
    // stack.
    const style = {
        transform: CSS.Translate.toString(transform),
        ...(isDragging && {
            "--drag-offset-x": `${transform?.x ?? 0}px`,
            "--drag-offset-y": `${transform?.y ?? 0}px`,
        }),
    };
    const className = `unfurl-block${isDragging ? " unfurl-block--dragging" : ""}`;
    return (_jsx("div", { ref: mergedRef, style: style, className: className, "data-unfurl-block-id": blockId, ...attributes, ...listeners, children: _jsx(BlockViewContainer, { blockId: blockId, dateChipMode: dateChipMode, inUnfurledStack: true }) }));
});
